import React, { useState } from "react";
import { Box, Card, IconButton, Tooltip } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import MicIcon from "@mui/icons-material/Mic";
import CampaignIcon from "@mui/icons-material/Campaign";
import CallIcon from "@mui/icons-material/Call";
import GroupIcon from "@mui/icons-material/Group";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import ForumIcon from "@mui/icons-material/Forum";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import MapsHomeWorkIcon from "@mui/icons-material/MapsHomeWork";
import ClearIcon from "@mui/icons-material/Clear";
import CropIcon from "@mui/icons-material/Crop";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import PersonPinCircleIcon from "@mui/icons-material/PersonPinCircle";
import SearchIcon from "@mui/icons-material/Search";
import UserSelectDialog from "../UserSelectDialog";
import { telemetry } from "globals/endpoints";
import loggerFactory from "globals/logger/logger-factory";
import CTLNotification from "application/components/CTLNotification";

const PALETTE_KEY = {
  error: "error",
  warning: "warning",
  warn: "warning",
  success: "success",
  info: "info",
  secondary: "grey",
};

function MAPActionButton({
  data,
  setShowSearch,
  mapTypeId,
  isDisable = false,
  locationUpdateColor = "warn",
  onRecordingPage,
  onPage,
  onDial,
  onGroupCall,
  onLiveGroupCall,
  onMessage,
  onEmergency,
  onNavigateHome,
  onAnchorLocation,
  MyLocation,
  drawingMode,
  toggleDrawing,
  selectedShape,
  isEditing,
  editShape,
  saveShape,
  deleteShape,
  showArea,
  setShowArea,
  showRoute,
  setShowRoute,
  setShowRouteList,
  setUserPaths,
}) {
  const theme = useTheme();
  const componentName = "MAP";
  const isSatelliteMode = mapTypeId === "satellite" || mapTypeId === "hybrid";
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });

  const renderActionButton = ({ tooltip, icon, color = "info", onClick, disabled = false }) => {
    const paletteKey = PALETTE_KEY[color] || color;
    const paletteColor =
      paletteKey === "grey"
        ? theme.palette.grey
        : theme.palette[paletteKey] || theme.palette.info;

    const mainColor =
      typeof paletteColor.main === "string"
        ? paletteColor.main
        : paletteColor[500] || theme.palette.info.main;

    return (
      <Tooltip key={tooltip} title={tooltip} placement="left" arrow>
        <Box
          component="span"
          sx={{
            width: 40,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: 1,
          }}
        >
          <IconButton
            onClick={onClick}
            disabled={disabled}
            sx={{
              width: 38,
              height: 38,
              borderRadius: "50%",
              border: `1px solid ${alpha(mainColor, disabled ? 0.3 : 0.4)}`,
              bgcolor: alpha(mainColor, disabled ? 0.08 : 0.1),
              color: disabled ? alpha(mainColor, 0.68) : mainColor,
              transition: "all 0.2s ease",
              "& .MuiSvgIcon-root": {
                fontSize: 20,
              },
              "&:hover": {
                bgcolor: alpha(mainColor, 0.22),
                transform: "translateY(-1px) scale(1.03)",
                boxShadow: `0 4px 12px ${alpha(mainColor, 0.32)}`,
              },
              "&.Mui-disabled": {
                opacity: 1,
                color: alpha(mainColor, 0.68),
              },
            }}
          >
            {icon}
          </IconButton>
        </Box>
      </Tooltip>
    );
  };

  const hasRenderableRoutePoints = (users = []) => {
    if (!Array.isArray(users)) {
      return false;
    }

    return users.some((routeUser) => {
      const validPoints = (routeUser?.location || []).filter((point) => {
        const latitude = Number.parseFloat(String(point?.latitude ?? "").trim());
        const longitude = Number.parseFloat(String(point?.longitude ?? "").trim());

        return Number.isFinite(latitude) && Number.isFinite(longitude);
      });

      return validPoints.length >= 2;
    });
  };

  const handleSelectUser = async (user) => {
    const payload = {
      extension: user.user.extensionNumber,
      fromDate: user.from,
      toDate: user.to
    };

    try {
      const res = await telemetry.post(`routeCoordinate/user`, payload);
      const userRouteData = Array.isArray(res?.data?.data) ? res.data.data : [];

      setOpenUserDialog(false);

      if (!hasRenderableRoutePoints(userRouteData)) {
        setUserPaths([]);
        setNotify({
          isOpen: true,
          message: "Route not created",
          type: "warning",
          pagename: componentName,
          status: "Selected user has null or invalid latitude/longitude data.",
        });
        return;
      }

      setUserPaths(
        userRouteData.map((item) => ({
          ...item,
          _fromDate: user.from,
          _toDate: user.to,
        })),
      );
      setNotify({
        isOpen: true,
        message: res?.data?.messageDetails || "User paths fetched successfully",
        type: "success",
        pagename: componentName,
        status: res?.data?.message,
      });
    } catch (error) {
      setOpenUserDialog(false);
      setUserPaths([]);
      loggerFactory.error(`Error fetching user paths: ${error.message}`, {
        error,
      });
       setNotify({
         isOpen: true,
         status: error?.response?.data?.messageDetails || "Failed to fetch user paths",
         type: "error",
         pagename: componentName,
         message: error?.response?.data?.message ,
       });
    }
  };

  return (
    <>
      <Box
        sx={{
          position: "absolute",
          top: 50,
          right: 5,
          zIndex: 14,
          display: "flex",
          borderRadius: 15,
          alignItems: "stretch",
        }}
      >
        <Card
          sx={{
            minHeight: 0,
            height: "auto",
            width: 50,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            borderRadius: 15,
            backgroundColor: isSatelliteMode
              ? alpha(theme.palette.grey[900], 0.68)
              : alpha(theme.palette.common.white, 0.72),
            border: `1px solid ${alpha(theme.palette.info.main, isSatelliteMode ? 0.32 : 0.18)}`,
            boxShadow: `0 6px 14px ${alpha(theme.palette.common.black, isSatelliteMode ? 0.25 : 0.12)}`,
            backdropFilter: "blur(8px)",
            p: 0.25,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "grid",
              gridAutoRows: "min-content",
              alignContent: "start",
              justifyItems: "center",
              rowGap: 1,
              overflowY: "auto",
              overflowX: "hidden",
              py: 0.35,
              px: 0,
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": {
                width: 0,
                height: 0,
                display: "none",
              },
            }}
          >
            {/* {renderActionButton({
              tooltip: "Search",
              icon: <SearchIcon />,
              color: "warning",
              onClick: () => setShowSearch((prev) => !prev),
            })} */}

            {renderActionButton({
              tooltip: "Recording Page",
              icon: <MicIcon />,
              color: "error",
              onClick: onRecordingPage,
            })}

            {renderActionButton({
              tooltip: "Page (Broadcast File)",
              icon: <CampaignIcon />,
              color: "warning",
              onClick: onPage,
            })}

            {!isDisable &&
              renderActionButton({
                tooltip: "Dial",
                icon: <CallIcon />,
                color: "success",
                onClick: onDial,
              })}

            {renderActionButton({
              tooltip: "Group Call",
              icon: <GroupIcon />,
              color: "success",
              onClick: onGroupCall,
            })}

            {renderActionButton({
              tooltip: "Live Group Call",
              icon: <LiveTvIcon />,
              color: "info",
              onClick: onLiveGroupCall,
            })}

            {!isDisable &&
              renderActionButton({
                tooltip: "Message",
                icon: <ForumIcon />,
                color: "info",
                onClick: onMessage,
              })}

            {!isDisable &&
              renderActionButton({
                tooltip: "Public Emergency",
                icon: <WarningAmberIcon />,
                color: "error",
                onClick: onEmergency,
              })}

            {renderActionButton({
              tooltip: "Navigate to Home Location",
              icon: <MyLocationIcon />,
              color: "warning",
              onClick: onNavigateHome || MyLocation,
            })}
{/* 
            {renderActionButton({
              tooltip: "Anchor to Current Location as Home",
              icon: <MapsHomeWorkIcon />,
              color: locationUpdateColor || "warning",
              onClick: onAnchorLocation,
            })} */}

            {renderActionButton({
              tooltip: drawingMode ? "Cancel Drawing" : "Draw Area",
              icon: drawingMode ? <ClearIcon /> : <CropIcon />,
              color: drawingMode ? "error" : "success",
              onClick: toggleDrawing,
              disabled: Boolean(selectedShape),
            })}

            {!isEditing
              ? renderActionButton({
                  tooltip: "Edit Area",
                  icon: <EditIcon />,
                  color: "info",
                  onClick: editShape,
                  disabled: !selectedShape,
                })
              : renderActionButton({
                  tooltip: "Save Changes",
                  icon: <SaveIcon />,
                  color: "success",
                  onClick: saveShape,
                })}

            {renderActionButton({
              tooltip: "Delete Area",
              icon: <DeleteForeverIcon />,
              color: "error",
              onClick: deleteShape,
              disabled: !selectedShape,
            })}
          {/* <Divider flexItem sx={dividerSx} />
          <Tooltip title="Zone" placement="left">
            <IconButton
              color={showArea ? "success" : "secondary"}
              onClick={() => setShowArea((prev) => !prev)}
              sx={{
                ...compactIconSx,
                "&:hover": {
                  ...compactIconSx["&:hover"],
                },
              }}
            >
              <PentagonIcon />
            </IconButton>
          </Tooltip>
          <Divider flexItem sx={dividerSx} />
          <Tooltip title="Route" placement="left">
            <IconButton
              color={showRoute ? "success" : "secondary"}
              onClick={() => setShowRoute((prev) => !prev)}
              sx={{
                ...compactIconSx,
                "&:hover": {
                  ...compactIconSx["&:hover"],
                },
              }}
            >
              <RouteIcon />
            </IconButton>
          </Tooltip>
          <Divider flexItem sx={dividerSx} />
          <Tooltip title="Select Route to Display" placement="left">
            <IconButton
              color="warning"
              onClick={() => setShowRouteList((prev) => !prev)}
              disabled={!showRoute}
              sx={{
                ...compactIconSx,
                "&:hover": {
                  ...compactIconSx["&:hover"],
                  boxShadow: `0 0 8px ${alpha(theme.palette.warning.main, 0.42)}`,
                },
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip> */}
            {renderActionButton({
              tooltip: "Marker Route",
              icon: <PersonPinCircleIcon />,
              color: "info",
              onClick: () => setOpenUserDialog((prev) => !prev),
            })}
          </Box>
        </Card>
      </Box>

      <UserSelectDialog
        open={openUserDialog}
        onClose={() => setOpenUserDialog(false)}
        users={data}
        handleSelectUser={handleSelectUser}
      />
      <CTLNotification notify={notify} setNotify={setNotify} />
    </>
  );
}

export default MAPActionButton;
